import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import {
  isCreating, isError, isSuccess, ResourcesItem, ResourcesList,
} from 'redux-and-the-rest';
import { differenceInYears } from 'date-fns';
import { generatePath } from 'react-router-dom';

import Header from '../shared/Header';
import TextInput from '../shared/TextInput';
import { UserModel } from '../../models/userModel';
import { createUser, getUser } from '../../redux/reducers/userReducer';
import { ApplicationState } from '../../redux/types';
import { isEducator, isRegisteredUser } from '../../utils/UserUtils';
import Checkbox from '../shared/CheckboxInput';
import { EDUCATOR_USER, GUARDIAN_USER, USER_ROLE } from '../../models/roleModel';
import { buildErrorsDictionary, ErrorMap } from '../../utils/ErrorsUtils';
import { Error } from '../../models/error';
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '../../utils/FormUtils';
import NewSessionHandler from '../new-session-handler/NewSessionHandler';
import RadioGroupInput from '../shared/RadioGroupInput';
import generateClientId from '../../redux/reducers/utils/generateClientId';
import { Routes } from '../../routes';
import Env from '../../utils/env';
import { getOrFetchIslands } from '../../redux/reducers/islandReducer';
import { IslandModel } from '../../models/islandModel';

const { DISABLE_EDUCATOR_REGISTRATION } = Env;

interface RegisterFormFields {
  email: string;
  password: string;
  role: USER_ROLE;
  firstName: string;
  lastName: string;
  confirmPassword: string;
  marketingOptIn: boolean;
  termsOptIn: boolean;
  dob: Date;
  schoolsName: string
}

const ROLE_OPTIONS = [{
  optionValue: GUARDIAN_USER,
  optionText: 'Parent',
}, {
  optionValue: EDUCATOR_USER,
  optionText: 'Educator',
}];

// A union of the keys of the registration model and user model.
type RegistrationErrorField = Extract<keyof RegisterFormFields, keyof UserModel>

interface Props {
  onSubmit(registerFormFields: RegisterFormFields): void
  user: ResourcesItem<UserModel>;
  islands: ResourcesList<IslandModel>;
}
const RegisterPanel = ({ onSubmit, user, islands }: Props) => {
  const {
    register, handleSubmit, formState: { errors }, control, getValues, setError, watch,
  } = useForm<RegisterFormFields>();

  const isCreatingUser = isCreating(user);
  const watchedRole = watch('role');

  const submit = handleSubmit((registerFormFields: RegisterFormFields) => {
    onSubmit(registerFormFields);
  });

  useEffect(() => {
    if (isError(user)) {
      const { status: { errors: remoteErrors } } = user;
      const errorMap: ErrorMap<RegistrationErrorField> = buildErrorsDictionary<RegistrationErrorField>(remoteErrors);

      errorMap.forEach((error: Error, key: RegistrationErrorField) => setError(key, {
        message: error.detail,
      }));
    }
  }, [user.status]);

  // A Registered user check is required as we may be successfully created as a Guest user.
  if (isSuccess(user) && isRegisteredUser(user)) {
    // On account creation, if a Parent a/c has no Islands, redirect Home, else redirect to their one Island
    const hasExistingGuestIsland = islands.items.length > 0;
    const islandId = hasExistingGuestIsland ? islands.items[0].values.id : undefined;
    const parentRedirectUrl = hasExistingGuestIsland ? generatePath(Routes.Islands.View, {
      id: islandId,
    }) : Routes.Home;
    const educatorRedirectUrl = Routes.Account.Schools.Index;

    const redirectTo = isEducator(user) ? educatorRedirectUrl : parentRedirectUrl;

    return <NewSessionHandler redirectTo={redirectTo} />;
  }

  return (
    <div className="panel box">
      <form className="form" onSubmit={submit}>
        <div className="form-section">
          <Header variant="h2" title="Sign-up for an Acount" />

          {DISABLE_EDUCATOR_REGISTRATION ? (
            <input
              type="hidden"
              value={GUARDIAN_USER}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...register('role')}
            />
          ) : (
            <>
              <div className="form-group">
                <Controller
                  name="role"
                  control={control}
                  rules={{
                    // eslint-disable-next-line max-len
                    required: 'Parents are families with a few children. Educators manage classrooms of students. Please indicate what type of user you are.',
                  }}
                  render={({
                    field: {
                      value, onChange, name,
                    },
                  }) => (
                    <RadioGroupInput
                      label="Type of User"
                      options={ROLE_OPTIONS}
                      value={value}
                      name={name}
                      onChange={onChange}
                      error={errors.role?.message}
                    />
                  )}
                />
              </div>

              {watchedRole === EDUCATOR_USER && (
                <div className="form-group">
                  <TextInput
                    label="School Name"
                    useFormRegisterReturn={register('schoolsName', {
                      required: 'School name is required',
                    })}
                    error={errors.schoolsName?.message}
                  />
                </div>
              )}
            </>
          )}

          <div className="form-group">
            <TextInput
              label="First Name"
              useFormRegisterReturn={register('firstName', {
                required: 'First name is required',
              })}
              error={errors.firstName?.message}
            />
          </div>

          <div className="form-group">
            <TextInput
              label="Last Name"
              useFormRegisterReturn={register('lastName', {
                required: 'Last name is required',
              })}
              error={errors.lastName?.message}
            />
          </div>

          <div className="form-group">
            <TextInput
              type="date"
              label="Date of Birth"
              useFormRegisterReturn={register('dob', {
                required: 'Your date of birth is required',
                validate: (dob: Date) => {
                  const dateOfBirth = new Date(dob);
                  const today = new Date(Date.now());
                  const age = differenceInYears(today, dateOfBirth);

                  return age >= 18 || 'You must be aged 18 or older to create an account';
                },
              })}
              error={errors.dob?.message}
            />
          </div>

          <div className="form-group">
            <TextInput
              label="Email address"
              useFormRegisterReturn={register('email', {
                required: 'Your email address is required',
              })}
              error={errors.email?.message}
            />
          </div>

          <div className="form-group">
            <TextInput
              require
              type="password"
              label="Password"
              useFormRegisterReturn={register('password', {
                required: 'A password is required to create an account',
                minLength: {
                  value: PASSWORD_MIN_LENGTH,
                  message: `Your password must be at least ${PASSWORD_MIN_LENGTH} characters long`,
                },
                maxLength: {
                  value: PASSWORD_MAX_LENGTH,
                  message: `Your password cannot be longer than ${PASSWORD_MAX_LENGTH} characters`,
                },
              })}
              error={errors.password?.message}
            />
          </div>

          <div className="form-group">
            <TextInput
              require
              type="password"
              label="Re-enter Password"
              useFormRegisterReturn={register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value) => value === getValues('password') || 'Your password confirmation does not match',
              })}
              error={errors.confirmPassword?.message}
            />
          </div>

          <div className="form-group">
            <Controller
              name="marketingOptIn"
              control={control}
              render={({
                field: {
                  value, onChange, name,
                },
              }) => (
                <Checkbox
                  name={name}
                  check={!!value}
                  onChange={onChange}
                >
                  Allow us to send you emails and marketing
                </Checkbox>
              )}
            />
          </div>

          <div className="form-group">
            <Controller
              control={control}
              name="termsOptIn"
              rules={{ required: 'Please confirm you agree to our Terms and Conditions' }}
              render={({
                field: {
                  value, onChange, name,
                },

              }) => (
                <Checkbox
                  error={errors.termsOptIn?.message}
                  name={name}
                  check={!!value}
                  onChange={onChange}
                >
                  Do you agree with our&nbsp;
                  <a
                    href="http://www.harpercollinschristian.com/terms/"
                    target="_blank"
                    rel="noreferrer"
                    title="terms and conditions"
                  >
                    Terms and conditions
                  </a>
                </Checkbox>
              )}
            />
          </div>
        </div>

        <div className="form-section form-actions">
          <button className="button primary" type="submit" disabled={isCreatingUser}>
            Create Account
          </button>
        </div>
      </form>

    </div>
  );
};

const mapReduxStateToProps = ({ users, islands }: ApplicationState) => ({
  onSubmit: ({
    email, firstName, lastName, password, marketingOptIn, termsOptIn, schoolsName, role,
  }: RegisterFormFields) => {
    // Temporary client side Id for to-be-created School.
    const clientSchoolId = generateClientId();

    // We can derive this to be true at this point rather than run the DOB through an age check function.
    const over18 = true;

    createUser({
      firstName,
      lastName,
      over18,
      email,
      password,
      marketingOptIn,
      termsOptIn,
      type: 'user',
      roles: [{ id: role, type: 'role' }],
      schools: schoolsName ? [{
        id: clientSchoolId,
        name: schoolsName,
        type: 'schools',
      }] : undefined,
      relationshipNames: ['roles', 'schools'],
      includeNames: ['schools'],
      include: 'schools',
    });
  },
  user: getUser(users),
  islands: getOrFetchIslands(islands),
});

export default connect(mapReduxStateToProps)(RegisterPanel);
